import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import Navbar from "../Components/Navbar";
import logo from "../Assets/logo.png";
import useOrders from "../hooks/useOrder";
import { AuthContext } from "../hooks/useLogin";

const OrderSummary = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);
  const userId = user?.id;

  const {
    address,
    setAddress,
    products,
    totalPrice,
    isSuccessModalOpen,
    setIsSuccessModalOpen,
    alertMessage,
    setAlertMessage,
    handleQuantityChange,
    handleSubmitOrder,
    isLoading, // New loading state from useOrders
  } = useOrders(userId, "add to cart", location?.state || {});

  useEffect(() => {
    if (!location?.state) {
      navigate("/cart"); // Redirect to cart if accessed directly
    }
  }, [location, navigate]);

  const handleAddAddress = () => {
    navigate("/youraddresses", {
      state: {
        selectedProducts: products,
        totalPrice: totalPrice,
      },
    });
  };

  return (
    <>
      <StickyContainer>
        <Navbar activeSection="ordersummary" />
        <HeaderContainer>
          <Header>
            <h1>Order Summary</h1>
            <p>✅ Secure and private data handling</p>
          </Header>
        </HeaderContainer>
        <ShippingAddress>
          {address ? (
            <div>
              <p><strong>{address.contact?.name || address.name}</strong></p>
              <p>{address.contact?.phone || address.phone_number}</p>
              <p>
                {address.location?.home || address.home},{" "}
                {address.location?.barangay || address.barangay},{" "}
                {address.location?.municipality || address.municipality}
              </p>
              <button onClick={handleAddAddress}>Change Address</button>
            </div>
          ) : (
            <button onClick={handleAddAddress}>Add shipping address +</button>
          )}
        </ShippingAddress>
      </StickyContainer>

      <Container>
        <SummaryContainer>
          <ScrollableOrderDetails>
            {products.length === 0 ? (
              <p style={{ textAlign: "center", fontSize: "18px" }}>
                No products selected. Go back to cart.
              </p>
            ) : (
              products.map((product, index) => (
                <Product key={product.id}>
                  <ProductImage
                    src={product.image ? `data:image/jpeg;base64,${product.image}` : logo}
                    alt={product.name}
                  />
                  <ProductInfo>
                    <h3>{product.name.charAt(0).toUpperCase() + product.name.slice(1)}</h3>
                    <p>₱{product.price}.00</p>
                    <QuantityContainer>
                      <QuantityButton onClick={() => handleQuantityChange(index, "decrement")}>-</QuantityButton>
                      <QuantityInput
                        type="number"
                        value={product.quantity}
                        onChange={(e) =>
                          handleQuantityChange(index, "custom", parseInt(e.target.value) || 1)
                        }
                      />
                      <QuantityButton onClick={() => handleQuantityChange(index, "increment")}>+</QuantityButton>
                    </QuantityContainer>
                    <p>Subtotal: ₱{product.price * product.quantity}.00</p>
                  </ProductInfo>
                </Product>
              ))
            )}
          </ScrollableOrderDetails>

          <FixedTotalSection>
            <TotalText>Total ({products.length} products)</TotalText>
            <SubmitContainer>
              <TotalAmount>₱{totalPrice}.00</TotalAmount>
              <SubmitButton onClick={() => handleSubmitOrder(navigate)} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner"></span> Submitting...
                  </>
                ) : (
                  "Submit Order"
                )}
              </SubmitButton>
            </SubmitContainer>
          </FixedTotalSection>
        </SummaryContainer>
      </Container>

      {isSuccessModalOpen && (
        <ModalOverlay>
          <ModalContent>
            <h2>🎉 Order Submitted Successfully!</h2>
            <p>Your order has been placed. Redirecting...</p>
            <CloseButton onClick={() => setIsSuccessModalOpen(false)}>OK</CloseButton>
          </ModalContent>
        </ModalOverlay>
      )}

      {alertMessage && (
        <ModalOverlay>
          <ModalContent>
            <h2>⚠️ Alert</h2>
            <p>{alertMessage}</p>
            <CloseButton onClick={() => setAlertMessage("")}>OK</CloseButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

// Updated SubmitButton with loading styles
const SubmitButton = styled.button`
  padding: 10px;
  background: green;
  color: #fff;
  font-size: 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${(props) => (props.disabled ? "green" : "#cc0000")};
  }

  // Spinner styles
  .spinner {
    border: 2px solid #fff;
    border-top: 2px solid transparent;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Other styled components remain unchanged
const QuantityButton = styled.button`
  padding: 5px 10px;
  border: none;
  background-color: #ddd;
  cursor: pointer;
  font-size: 16px;
`;

const QuantityContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const QuantityInput = styled.input`
  width: 40px;
  text-align: center;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 2px;
`;

const Container = styled.div`
  font-family: Arial, sans-serif;
  background-color: #eaf4e1;
  min-height: 100vh;
  padding: 0px;
  margin-top: -10px;
`;

const StickyContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: white;
`;

const SummaryContainer = styled.div`
  max-width: 1670px;
  margin: 20px auto;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  max-height: 650px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 10px;

  h1 {
    font-size: 24px;
    margin: 0;
    color: white;
  }

  p {
    font-size: 14px;
    color: white;
    margin: 5px 0 0;
  }
`;

const HeaderContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  width: 97%;
  height: 50px;
  background-color: green;
  padding: 5px 20px;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ShippingAddress = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 15px;
  margin-bottom: 15px;
  font-size: 12px;

  p {
    font-size: 12px;
    margin: 2px 0;
    text-align: center;
  }

  button {
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    border-radius: 5px;
    padding: 10px 30px;
    font-size: 12px;
    cursor: pointer;
    margin-top: 5px;
  }

  button:hover {
    background-color: #e6e6e6;
  }
`;

const ScrollableOrderDetails = styled.div`
  flex-grow: 1;
  padding: 10px;
  max-height: calc(100vh - 250px);
  border-top: 1px solid #e5e5e5;
`;

const Product = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  border-bottom: 1px solid #e5e5e5;
  padding-bottom: 10px;
`;

const Checkbox = styled.input.attrs({ type: "checkbox", checked: true })`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #e60000;
  appearance: none;
  cursor: pointer;
  background-color: ${(props) => (props.checked ? "#e60000" : "#fff")};
  display: flex;
  align-items: center;
  justify-content: center;

  &:checked::after {
    content: "✓";
    color: white;
    font-size: 15px;
  }
`;

const ProductImage = styled.img`
  width: 220px;
  height: 190px;
  border-radius: 8px;
  border: 2px solid #eaf4e1;
  display: block;
  margin-left: 30px;
`;

const ProductInfo = styled.div`
  flex: 1;
  align-items: center;
  gap: 3px;
  position: relative;
  left: 30px;

  h3 {
    margin: 0;
    font-size: 40px;
    color: #333;
  }

  p {
    margin: 5px 0;
    font-size: 20px;
    color: #666;
  }
`;

const FixedTotalSection = styled.div`
  position: sticky;
  bottom: 0;
  background: white;
  padding: 10px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;
`;

const TotalText = styled.div`
  flex: 1;
  text-align: left;
  font-size: 20px;
  color: green;
  font-weight: bold;
`;

const TotalAmount = styled.div`
  text-align: right;
  font-size: 24px;
  color: green;
  font-weight: bold;
`;

const SubmitContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
`;

const CloseButton = styled.button`
  background: #4CAF50;
  color: white;
  padding: 10px 15px;
  border: none;
  margin-top: 10px;
  cursor: pointer;
  border-radius: 5px;
  font-size: 16px;
  &:hover {
    background: #45a049;
  }
`;

export default OrderSummary;